import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryDataSource } from '../../api/query';
import { raporBul, sourceIdBul } from '../../utils/raporSonek';
import { KpiCard, Delta, fmtNum, fmtTRY } from '../yeni-design/primitives';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem';
// CONTROLLER RULING F1: Donem ayri modulde
import { lokasyonDestekli, lokasyonParam, subeSuz } from './subeSuzgeci';
import { KapsamNotu } from './KapsamNotu';

interface DonemSatiri {
  metrik: string;
  bu_donem: number;
  onceki_donem: number;
  degisim_pct: number;
}

interface KategoriSatiri {
  kategori: string;
  ciro: number;
  miktar: number;
  kar: number;
  pay_yuzde: number;
}

interface KanalSatiri {
  tarih: string;
  // 040 fix4'ten beri lokasyon TASIYOR; sube suzgeci bunu kullanir.
  lokasyon: string;
  kanal_grup: string;
  kanal: string;
  fis_sayisi: number;
  net_ciro: number;
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
  /** null = tum subeler. 040 `lokasyon` tasidigi icin ISTEMCIDE suzulur. */
  sube?: string | null;
}

/**
 * Katman 1 - ozet serit: dort olcu (net ciro, fis, sepet ortalamasi, brut kar).
 *
 * Sepet ortalamasi K2'nin kapanis sartina gore YALNIZ kanal_grup='Magaza'
 * satirlarindan hesaplanir. "Magaza" kovasi nakit perakendeyi, firmanin
 * kendi online satisini, alti yemek karti operatorunu ve kurumsal
 * musterileri karistirir; yemek karti operatorleri ayda TEK, yuz binlerce
 * liralik toplu fatura keser. Bu "fis"leri sepet ortalamasina katmak
 * ortalamayi sisirir (canli olcum: 381,42 ₺ yerine gercek 331,59 ₺,
 * %15,0 fazla basim).
 */
export function OzetSerit({ reports, donem, sube = null }: Props) {
  const params = { '@BASTARIH': donem.bas, '@BITTARIH': donem.bit };
  // S200: rapor NESNESI de lazim - `lokasyonDestekli` kararini raporun KENDI
  // parametre tanimindan turetiyoruz, elle yazmiyoruz. Bir ds'e @LOKASYON
  // eklendigi anda suzgec calisir ve kapsam notu kendiliginden susar.
  const raporDonem = raporBul(reports, 12);
  const raporKategori = raporBul(reports, 15);
  const idDonem = raporDonem?.sourceId ?? undefined;
  const idKategori = raporKategori?.sourceId ?? undefined;
  // CONTROLLER RULING F4: sonek 35, 995035'in ("Acik Hesaplar", baska bir
  // firma) sonek carpismasi yuzunden 990040'a tasindi ve 990035 PRODUCTIONDAN
  // SILINDI. Ad kontrolu de zorunlu - tutmazsa blok sessizce gizlenir, yanlis
  // firmanin verisini basmaz (bkz. KanalBlok.tsx, ayni desen).
  const idKanal = sourceIdBul(reports, 40, 'Satış Kanalı Özeti');

  // 🔴 CANLIDA OLCULDU (2026-08-21): bu serit sube suzgecine HIC baglanmamisti.
  // Secicide "ATAKENT" yazarken NET CIRO firma genelini (₺8.025.455,64)
  // basiyordu - subenin gercek kanal toplami ₺4.385.426, yani 1,83x fazla.
  // S198 envanteri ekranin 10 ds'ini saydi ama seridi besleyen 012'yi hic
  // listelemedi; 015 ise @LOKASYON'u zaten destekledigi halde parametre
  // gonderilmiyordu. Artik her olcu ya SUZULUR ya da ETIKETLENIR.
  const donemDestekli = lokasyonDestekli(raporDonem);
  const kategoriDestekli = lokasyonDestekli(raporKategori);
  const paramsDonem = { ...params, ...lokasyonParam(sube, donemDestekli) };
  const paramsKategori = { ...params, ...lokasyonParam(sube, kategoriDestekli) };

  // throwOnError: false — burada bir global varsayilani GERI ALMIYOR: Template panelinin
  // main.tsx'i QueryClient'a throwOnError:true VERMEZ (yalniz staleTime/retry ayarli).
  // O desen Yerlice panelinin lib/sorguIstemcisi.ts'inde var, bu panelde degil. Yine de
  // acikca yazildi: niyet kod okuyucuya gorunur olsun — bir kaynak duserse serit tumden
  // cokmesin, o olcu bos kalsin.
  const donemQ = useQuery({
    // @LOKASYON queryKey'e DE girer: girmezse ikinci sube birincinin
    // onbellegini yer ve ekran gene firma geneli basar.
    queryKey: ['sa-ozet-donem', idDonem, paramsDonem],
    queryFn: () => queryDataSource<DonemSatiri>({ sourceId: idDonem!, parameters: paramsDonem }),
    enabled: !!idDonem,
    throwOnError: false,
  });
  const kategoriQ = useQuery({
    queryKey: ['sa-ozet-kategori', idKategori, paramsKategori],
    queryFn: () =>
      queryDataSource<KategoriSatiri>({ sourceId: idKategori!, parameters: paramsKategori }),
    enabled: !!idKategori,
    throwOnError: false,
  });
  const kanalQ = useQuery({
    queryKey: ['sa-ozet-kanal', idKanal, params],
    queryFn: () => queryDataSource<KanalSatiri>({ sourceId: idKanal!, parameters: params }),
    enabled: !!idKanal,
    throwOnError: false,
  });

  // Bos durum kurali: gerekli hicbir kaynak yoksa serit hic basilmaz.
  if (!idDonem && !idKategori && !idKanal) return null;

  const donemSatir = (metrik: string) =>
    donemQ.data?.data.find((r) => String(r.metrik) === metrik);

  const ciro = donemSatir('Ciro');
  const fis = donemSatir('Fiş');

  // Kaynak eksik/yukleniyor/hatali VEYA hic satir yoksa "veri yok" (null) — sayi
  // basmiyoruz, siblingler gibi '—'. Gercek bir sifir (satir var, kar=0) ise null
  // DEGIL, 0 dondurur ve '₺0' basilir — "veri yok" ile "veri sifir" birbirine karismaz.
  const kategoriSatirlari = kategoriQ.data?.data;
  const kar =
    kategoriSatirlari && kategoriSatirlari.length > 0
      ? kategoriSatirlari.reduce((t, r) => t + Number(r.kar ?? 0), 0)
      : null;

  const magaza = subeSuz(kanalQ.data?.data ?? [], sube)
    .filter((r) => String(r.kanal_grup) === 'Mağaza')
    .reduce(
      (a, r) => ({
        ciro: a.ciro + Number(r.net_ciro ?? 0),
        fis: a.fis + Number(r.fis_sayisi ?? 0),
      }),
      { ciro: 0, fis: 0 },
    );
  const sepet = magaza.fis > 0 ? magaza.ciro / magaza.fis : null;

  /**
   * KpiCard'in alt satiri: varsa degisim rozeti, gerekiyorsa kapsam notu.
   * Ikisi de yoksa `undefined` doner - KpiCard bos bir satir cizmesin.
   * Sepet olcusu bu yardimciyi KULLANMAZ: kaynagi (040) @LOKASYON bildirmese
   * de satirlari `lokasyon` tasidigi icin ISTEMCIDE suzuluyor, yani sayi
   * zaten subeye ait. Ona not basmak dogru sayiyi yanlis etiketlemek olurdu.
   */
  const altBilgi = (rozet: ReactNode, destekli: boolean): ReactNode => {
    const notGerekli = sube !== null && !destekli;
    if (!rozet && !notGerekli) return undefined;
    return (
      <>
        {rozet}
        <KapsamNotu sube={sube} destekli={destekli} />
      </>
    );
  };

  return (
    <div data-testid="blok-ozet" className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <div data-testid="olcu-ciro">
        <KpiCard
          label="Net ciro"
          prefix=""
          value={ciro ? fmtTRY(ciro.bu_donem) : '—'}
          sub={altBilgi(
            ciro ? <Delta value={ciro.degisim_pct} decimals={2} /> : null,
            donemDestekli,
          )}
        />
      </div>
      <div data-testid="olcu-fis">
        <KpiCard
          label="Fiş"
          prefix=""
          value={fis ? fmtNum(fis.bu_donem) : '—'}
          sub={altBilgi(
            fis ? <Delta value={fis.degisim_pct} decimals={2} /> : null,
            donemDestekli,
          )}
        />
      </div>
      <div data-testid="olcu-sepet">
        {/* fmtTRY kendi ₺ isaretini basar; KpiCard'in varsayilan prefix='₺' burada
            CIFT sembole yol acmasin diye prefix="" ile gecersiz kilindi. */}
        <KpiCard
          label="Sepet ortalaması"
          prefix=""
          value={sepet !== null ? fmtTRY(sepet) : '—'}
          sub="Mağaza kanalı; kurumsal ve yemek kartı faturaları hariç"
        />
      </div>
      <div data-testid="olcu-kar">
        {/* Brut kar bir PARA degeri: fmtNum .toString() kullandigi icin kayan
            nokta hatasini oldugu gibi basiyordu (canlida 1.950.597,7499999998).
            fmtTRY iki haneye yuvarlar; kendi ₺ isaretini bastigi icin prefix=""
            ile KpiCard'in varsayilani gecersiz kilinir (sepet olcusuyle ayni desen). */}
        <KpiCard
          label="Brüt kâr"
          prefix=""
          value={kar !== null ? fmtTRY(kar) : '—'}
          sub={altBilgi(null, kategoriDestekli)}
        />
      </div>
    </div>
  );
}
